/**
 * DealTagManagementDialog - Dialog para gerenciar tags de negócios
 * 
 * Versão: 1.0.0
 * - CRUD completo de tags
 * - Seletor de cor com preset
 * - Validação com zod
 * 
 * RELEASE: 2026-01-07
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Pencil, Trash2, Plus, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  useDealTags,
  useCreateDealTag,
  useUpdateDealTag,
  useDeleteDealTag,
  DealTag,
} from '@/hooks/useDealTags';
import { DealTagBadge } from './DealTagBadge';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';

// Schema de validação
const tagSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(50, 'Nome muito longo'),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Cor inválida'),
});

type TagFormData = z.infer<typeof tagSchema>;

// Cores preset pastéis para seleção rápida
const PRESET_COLORS = [
  '#fca5a5', // vermelho pastel
  '#fdba74', // laranja pastel
  '#fde047', // amarelo pastel
  '#86efac', // verde pastel
  '#5eead4', // teal pastel
  '#93c5fd', // azul pastel
  '#c4b5fd', // roxo pastel
  '#f9a8d4', // rosa pastel
  '#d1d5db', // cinza claro
  '#e9d5ff', // lavanda pastel
];

// Props do componente
interface DealTagManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  // Permissões granulares (default true mantém retrocompatibilidade)
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

export function DealTagManagementDialog({
  open,
  onOpenChange,
  tenantId,
  canCreate = true,
  canEdit = true,
  canDelete = true,
}: DealTagManagementDialogProps) {
  // Estados
  const [editingTag, setEditingTag] = useState<DealTag | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Hook de confirmação
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
  
  // Queries e mutations
  const { data: tags = [], isLoading } = useDealTags(tenantId);
  const createMutation = useCreateDealTag();
  const updateMutation = useUpdateDealTag();
  const deleteMutation = useDeleteDealTag();
  
  // Form
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TagFormData>({
    resolver: zodResolver(tagSchema),
    defaultValues: {
      name: '',
      color: '#93c5fd',
    },
  });
  
  const selectedColor = watch('color');
  
  // Resetar form quando fecha
  useEffect(() => {
    if (!open) {
      reset();
      setEditingTag(null);
      setIsCreating(false);
    }
  }, [open, reset]);
  
  // Handlers
  const handleCreate = () => {
    reset({ name: '', color: '#93c5fd' });
    setEditingTag(null);
    setIsCreating(true);
  };
  
  const handleEdit = (tag: DealTag) => {
    setEditingTag(tag);
    setIsCreating(false);
    reset({ name: tag.name, color: tag.color });
  };
  
  const handleCancel = () => {
    reset();
    setEditingTag(null);
    setIsCreating(false);
  };
  
  // Handler de exclusão com diálogo de confirmação moderno
  const handleDelete = async (tag: DealTag) => {
    const confirmed = await confirmDialog({
      title: `Excluir tag "${tag.name}"?`,
      description: 'A tag será removida de todos os negócios associados.',
      warningMessage: 'Esta ação não pode ser desfeita.',
      variant: 'danger',
      confirmText: 'Excluir Tag',
      cancelText: 'Cancelar',
    });
    
    if (confirmed) {
      deleteMutation.mutate({ id: tag.id, tenantId });
    }
  };
  
  const onSubmit = async (data: TagFormData) => {
    if (editingTag) {
      // Atualizar
      await updateMutation.mutateAsync({
        id: editingTag.id,
        name: data.name,
        color: data.color,
      });
    } else {
      // Criar
      await createMutation.mutateAsync({
        tenant_id: tenantId,
        name: data.name,
        color: data.color,
      });
    }
    
    handleCancel();
  };
  
  const isSaving = createMutation.isPending || updateMutation.isPending;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Gerenciar Tags</DialogTitle>
          <DialogDescription>
            Crie e edite tags para categorizar seus negócios.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Lista de tags existentes */}
          <ScrollArea className="h-[200px] pr-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : tags.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Nenhuma tag criada ainda.
              </div>
            ) : (
              <div className="space-y-2">
                {tags.map((tag) => (
                  <div
                    key={tag.id}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <DealTagBadge name={tag.name} color={tag.color} size="md" />
                    
                    <div className="flex items-center gap-1">
                      {/* Botão editar - respeita can_edit */}
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleEdit(tag)}
                          disabled={deleteMutation.isPending}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {/* Botão excluir - respeita can_delete */}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(tag)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <Separator />
          
          {/* Formulário de criação/edição */}
          {(isCreating || editingTag) ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tag-name">Nome da tag</Label>
                <Input
                  id="tag-name"
                  placeholder="Ex: Cliente VIP"
                  {...register('name')}
                  autoFocus
                />
                {errors.name && (
                  <p className="text-xs text-destructive">{errors.name.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Cor</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-7 h-7 rounded-full border-2 transition-all ${
                        selectedColor === color
                          ? 'border-foreground scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setValue('color', color)}
                      aria-label={`Selecionar cor ${color}`}
                    />
                  ))}
                  
                  {/* Input de cor customizada */}
                  <Input
                    type="color"
                    className="w-7 h-7 p-0 border-0 cursor-pointer"
                    {...register('color')}
                  />
                </div>
                {errors.color && (
                  <p className="text-xs text-destructive">{errors.color.message}</p>
                )}
              </div>
              
              {/* Preview */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Preview:</span>
                <DealTagBadge
                  name={watch('name') || 'Tag'}
                  color={selectedColor}
                  size="md"
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleCancel}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingTag ? 'Salvar' : 'Criar'}
                </Button>
              </div>
            </form>
          ) : (
            // Botão "Nova Tag" só aparece quando o usuário tem permissão de criar
            canCreate && (
              <Button
                variant="outline"
                className="w-full"
                onClick={handleCreate}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Tag
              </Button>
            )
          )}
        </div>
        
        {/* Diálogo de confirmação de exclusão */}
        {ConfirmDialogComponent}
      </DialogContent>
    </Dialog>
  );
}
